/**
 * Temporary diagnostic logging for the recurring iOS Chrome "tap audition ->
 * FeedV2 opens -> auto-bounces back to TagPageV2" bug.
 *
 * Two prior CSS-only fixes (overscroll-behavior-x, then touch-action: pan-y)
 * both failed to stop it, which means the root cause is still unconfirmed.
 * This logs the actual event sequence around the bounce - including whether
 * any real touch input occurred - so the next fix is based on evidence
 * instead of another guess.
 *
 * Only active when `?debug=1` is on the URL (matches the existing
 * isDebugMode convention in FeedV2.vue/TagPageV2.vue). Log entries persist in
 * sessionStorage (they survive the SPA navigation that the bug itself
 * causes) and are dumped to the console + cleared the next time TagPageV2
 * mounts with debug on, so a single on-device repro capturing the console
 * after the bounce shows the full trace.
 *
 * Remove this file and its usages once the bug is confirmed fixed.
 */

import { reactive } from 'vue'

const STORAGE_KEY = 'reelsSwipeBackDebugLog'
const DEBUG_MODE_STORAGE_KEY = 'reelsDebugMode'
const MAX_ENTRIES = 200

/**
 * `isDebugMode` in TagPageV2.vue/FeedV2.vue only checks the URL's `?debug=1`
 * param, but `goToFeed`'s `router.visit(feedUrl(post))` doesn't carry that
 * param along - so FeedV2 silently loses debug mode (and all its logging,
 * including the suspected replaceState culprit) on every navigation from
 * TagPageV2. This persists the flag in sessionStorage so it survives the
 * SPA navigation regardless of the target URL's query string.
 *
 * Call once per page with the page's own URL-derived debug boolean; returns
 * the effective value (URL param OR previously-persisted flag).
 */
export function persistDebugMode(urlSaysDebug) {
    if (urlSaysDebug) {
        sessionStorage.setItem(DEBUG_MODE_STORAGE_KEY, '1')
        return true
    }
    return sessionStorage.getItem(DEBUG_MODE_STORAGE_KEY) === '1'
}

let bootTime = null

/** Shared reactive log, module-scoped so both TagPageV2 and FeedV2 render
 *  the same live list in their on-screen debug overlays - Chrome for iOS
 *  has no remote-debugging console access, so a screenshot/recording of
 *  this is often the only way to get the trace off the device. */
export const swipeBackDebugEntries = reactive([])

const readLog = () => {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]')
    } catch {
        return []
    }
}

const writeLog = (entries) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)))
    } catch {
        // sessionStorage full/unavailable - ignore, this is diagnostic-only
    }
}

export function useReelsSwipeBackDebug(isDebugMode, tag) {
    if (bootTime === null) {
        bootTime = performance.now()
    }

    const log = (event, data = {}) => {
        if (!isDebugMode?.value) {
            return
        }

        const entry = {
            t: Math.round(performance.now() - bootTime),
            wall: Date.now(),
            tag,
            event,
            historyLength: window.history.length,
            visibility: document.visibilityState,
            path: window.location.pathname,
            ...data,
        }

        console.log(`🐛[swipe-back] ${tag} ${event}`, entry)
        swipeBackDebugEntries.push(entry)
        if (swipeBackDebugEntries.length > MAX_ENTRIES) {
            swipeBackDebugEntries.splice(0, swipeBackDebugEntries.length - MAX_ENTRIES)
        }

        const entries = readLog()
        entries.push(entry)
        writeLog(entries)
    }

    /** Dumps and clears the persisted log. Call on TagPageV2 mount to see
     *  the full trace after a bounce-back, since it's the page the user
     *  lands back on. Entries already in `swipeBackDebugEntries` (this
     *  session's, i.e. FeedV2's, own logged events) are kept - only the
     *  sessionStorage copy is cleared. */
    const flushLog = () => {
        if (!isDebugMode?.value) {
            return
        }

        const entries = readLog()
        if (entries.length) {
            console.log(`🐛[swipe-back] ${tag} flushing ${entries.length} persisted entries from previous page:`)
            console.table(entries)
        }
        writeLog([])
    }

    return { log, flushLog }
}

/** Base keys every entry always has - used to find the "extra" per-event
 *  data (component, url, errors, completed/cancelled/...) so the on-screen
 *  panel can render it generically instead of only recentTouches/lastTouch. */
const BASE_ENTRY_KEYS = ['t', 'wall', 'tag', 'event', 'historyLength', 'visibility', 'path', 'recentTouches', 'lastTouch']

export function entryExtras(entry) {
    const extras = {}
    for (const key in entry) {
        if (!BASE_ENTRY_KEYS.includes(key)) {
            extras[key] = entry[key]
        }
    }
    return extras
}

/**
 * Tracks raw touch events on `document` in `window.__reelsLastTouch` /
 * `window.__reelsTouchHistory`. `window` survives Inertia's SPA navigation
 * (it's not a full page reload), so FeedV2 can inspect touches that started
 * on TagPageV2, and later code can check whether any *real* touch input
 * happened around the time of a bounce - as opposed to it being triggered
 * by a script-driven history.replaceState() call with no touch involved.
 *
 * Idempotent: safe to call from both TagPageV2 and FeedV2's onMounted
 * without installing duplicate listeners.
 */
export function installReelsTouchTracker(isDebugMode) {
    if (!isDebugMode?.value || window.__reelsTouchTrackerInstalled) {
        return
    }
    window.__reelsTouchTrackerInstalled = true
    window.__reelsTouchHistory = window.__reelsTouchHistory || []

    const record = (type, e) => {
        const touch = e.touches[0] || e.changedTouches[0]
        const entry = {
            type,
            t: Math.round(performance.now() - (bootTime ?? performance.now())),
            x: touch?.clientX ?? null,
            y: touch?.clientY ?? null,
            distanceFromLeftEdge: touch ? touch.clientX : null,
            path: window.location.pathname,
        }
        window.__reelsLastTouch = entry
        window.__reelsTouchHistory.push(entry)
        window.__reelsTouchHistory = window.__reelsTouchHistory.slice(-50)
        console.log(`🐛[swipe-back] touch:${type}`, entry)
    }

    document.addEventListener('touchstart', (e) => record('touchstart', e), { passive: true })
    document.addEventListener('touchend', (e) => record('touchend', e), { passive: true })
    document.addEventListener('touchcancel', (e) => record('touchcancel', e), { passive: true })
}

/**
 * Global Inertia router event listeners, as a broader net alongside the
 * per-visit onStart/onCancel/onError/onSuccess/onFinish callbacks on
 * goToFeed's router.visit() - in case the bounce isn't actually going
 * through that visit's own lifecycle at all (e.g. a network error or
 * exception surfaced elsewhere). Idempotent like installReelsTouchTracker.
 */
export function installReelsRouterEventLogger(router, isDebugMode, log) {
    if (!isDebugMode?.value || window.__reelsRouterLoggerInstalled) {
        return
    }
    window.__reelsRouterLoggerInstalled = true

    // 'before' fires synchronously before every single visit, however it
    // was triggered (Link click, router.visit() call anywhere in the app,
    // a bare <a> Inertia happens to intercept, etc). Capturing a stack
    // trace here is a lot more reliable than grepping for router.visit()
    // call sites by hand - it'll show the real origin even if it's a call
    // site we haven't found (e.g. inside a child modal component).
    router.on('before', (event) => log('router:before', {
        url: event.detail?.visit?.url?.href,
        stack: new Error().stack
            ?.split('\n')
            .slice(1, 8)
            .map((line) => line.trim().slice(0, 60))
            .join(' | '),
    }))
    router.on('start', (event) => log('router:start', { url: event.detail?.visit?.url?.href }))
    router.on('networkError', (event) => log('router:networkError', { error: String(event.detail?.error) }))
    router.on('httpException', (event) => log('router:httpException', { status: event.detail?.response?.status }))
    router.on('invalid', (event) => log('router:invalid', { status: event.detail?.response?.status }))
    router.on('navigate', (event) => log('router:navigate', { url: event.detail?.page?.url, component: event.detail?.page?.component }))
}
